/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyGetAdditionalDocumentsByCandidateIdQuery } from '$store/modules/additional-documents';
import { useLazyGetAdditionalTagsByCandidateIdQuery } from '$store/modules/additional-tags';
import {
  useLazyGetAdditionalVideosByCandidateIdQuery,
  useLazyGetEnglishAssessmentQuery,
} from '$store/modules/additional-videos';
import { useLazyGetCandidateEducationQuery } from '$store/modules/candidate-education';
import { useLazyGetCandidateJobInterestQuery } from '$store/modules/candidate-job-interest';
import { useLazyGetCandidateLanguagesQuery } from '$store/modules/candidate-language';
import { useLazyGetCandidateSkillsQuery } from '$store/modules/candidate-skill';
import { useLazyGetCvQuery } from '$store/modules/uploads';
import { useLazyGetWorkHistoryByCandidateIdQuery } from '$store/modules/work-experience';
import { userLanguage } from '$translations/index';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { useLazyGetCandidateInterviewByCPSQuery } from '$store/modules/interview';
import { LoadOnePositionOutput } from '$store/modules/companies';
import {
  SmartInterviewVideoStatus,
  useLazyGetCandidateSmartInterviewsVideosQuery,
} from '$store/modules/smart-interviews';
import { useLazyGetCandidateReferralsQuery } from '$store/modules/candidate-referrals';
import { ReferralStatus } from '$constants/ReferralStatus';

export type CandidateHydrationFlags = {
  wantsEducation?: boolean;
  wantsLanguages?: boolean;
  wantsTags?: boolean;
  wantsEnglish?: boolean;
  wantsInterviewStatus?: boolean;
};

const HYDRATION_CONCURRENCY = 6;

type CandidatePositionStatusItem = NonNullable<
  LoadOnePositionOutput['CandidatesPositionStatus']
>[number];

/** What was fetched for one candidate; failed when hydrating it threw */
type HydratedCandidate = { fields: Record<string, any>; failed?: boolean };

type ResolvedFlags = Required<CandidateHydrationFlags>;

/** Fields fetched only when the page asks for them, keyed by the flag that asks */
const FLAG_FIELDS: Array<[keyof ResolvedFlags, string]> = [
  ['wantsEducation', 'CandidateEducation'],
  ['wantsLanguages', 'CandidateLanguages'],
  ['wantsTags', 'AdditionalTags'],
  ['wantsEnglish', 'Videos'],
  ['wantsInterviewStatus', 'CandidateInterviewStatus'],
];

const sortByHighlightThenByCreateTime = (
  a: CandidatePositionStatusItem,
  b: CandidatePositionStatusItem
) => {
  // Sort by highlight first
  if (a.Highlight && !b.Highlight) return -1;
  if (!a.Highlight && b.Highlight) return 1;

  // If both have the same highlight value, sort by create time
  const timeA = new Date(a.CreateTime).getTime();
  const timeB = new Date(b.CreateTime).getTime();
  return timeB - timeA;
};

/** The position's candidates in display order, each candidate once */
const sortedUniqueCandidates = (position: PositionInternalDto | undefined) => {
  const seen = new Set<number>();
  return [...(position?.CandidatesPositionStatus || [])]
    .filter((item) => item.Candidate?.Id && item.Candidate?.User)
    .sort(sortByHighlightThenByCreateTime)
    .filter((item) => {
      if (seen.has(item.Candidate.Id)) return false;
      seen.add(item.Candidate.Id);
      return true;
    });
};

/** null when nothing is fetched yet, else the flag fields still to fetch */
const missingFlagFields = (
  hydrated: HydratedCandidate | undefined,
  flags: ResolvedFlags
): string[] | null => {
  if (!hydrated) return null;
  if (hydrated.failed) return [];
  return FLAG_FIELDS.filter(
    ([flag, field]) => flags[flag] && !(field in hydrated.fields)
  ).map(([, field]) => field);
};

const needsFetch = (
  hydrated: HydratedCandidate | undefined,
  flags: ResolvedFlags
) => {
  const missing = missingFlagFields(hydrated, flags);
  return missing === null || missing.length > 0;
};

/** The SearchFilter rule: these tabs show candidates of every status */
const filtersByStatus = (status: string | undefined) =>
  !!status && status.toLowerCase() !== 'find' && status.toLowerCase() !== 'all';

const hasStatus = (item: CandidatePositionStatusItem, status: string) =>
  item.Status?.toLowerCase() === status.toLowerCase();

/** The candidate as the position lists it, trimmed to what the cards use */
const positionCandidate = (item: CandidatePositionStatusItem) => {
  const tempData: any = { ...item.Candidate };
  tempData.CandidatesPositionStatus = [
    {
      Status: item.Status,
      CreateTime: item.CreateTime,
      Id: item.Id,
      Highlight: item.Highlight,
    },
  ];

  /** Parent props */
  delete tempData.AddresLine1;
  delete tempData.AddresLine2;
  delete tempData.AgreedTermsOfServiceDate;
  delete tempData.Completed;
  delete tempData.IsFirstLogin;
  delete tempData.LinkedinUrl;
  delete tempData.WhatsAppNumber;

  /** User props */
  tempData.User = { ...item.Candidate.User };
  delete tempData.User.EmailVerified;
  delete tempData.User.ConfirmToken;
  delete tempData.User.Id;
  delete tempData.User.Phone;
  delete tempData.User.SignUpType;

  /** User ProfilePicture */
  tempData.User.ProfilePicture = { ...tempData.User.ProfilePicture };
  delete tempData.User.ProfilePicture.Key;

  return tempData;
};

const mapEducation = (data: any) =>
  [...(data || [])].map((educationData: any) => ({
    Id: educationData?.Id,
    DegreeName: educationData?.DegreeName?.val,
    DegreeType: educationData?.DegreeType?.val,
  }));

const mapLanguages = (data: any) =>
  (data || []).map((singleLanguage: any) => ({
    Id: singleLanguage?.Id,
    LanguageName: singleLanguage?.LanguageName?.val,
  }));

const mapTags = (data: any) =>
  (data || []).map((singleAdditionalTag: any) => ({
    Id: singleAdditionalTag.Id,
    Name: singleAdditionalTag.title,
  }));

const mapEnglishVideos = (data: any) =>
  (data || []).map((singleEnglishAssesment: any) => ({
    EnglishAssessment: singleEnglishAssesment?.Score
      ? { Score: singleEnglishAssesment.Score }
      : null,
    Id: singleEnglishAssesment?.AdditionalVideo?.Id || null,
    Key: singleEnglishAssesment?.AdditionalVideo?.Key || null,
    Type: 0,
    Url: singleEnglishAssesment?.AdditionalVideo?.Url || null,
  }));

const GetCandidateDataByMultipleFetch = (
  position: PositionInternalDto | undefined,
  flags?: CandidateHydrationFlags,
  /** The status tab the user is looking at; its candidates load first */
  activeStatus?: string
) => {
  const wantsEducation = flags?.wantsEducation ?? true;
  const wantsLanguages = flags?.wantsLanguages ?? true;
  const wantsTags = flags?.wantsTags ?? true;
  const wantsEnglish = flags?.wantsEnglish ?? true;
  const wantsInterviewStatus = flags?.wantsInterviewStatus ?? true;
  const resolvedFlags = useMemo<ResolvedFlags>(
    () => ({
      wantsEducation,
      wantsLanguages,
      wantsTags,
      wantsEnglish,
      wantsInterviewStatus,
    }),
    [
      wantsEducation,
      wantsEnglish,
      wantsInterviewStatus,
      wantsLanguages,
      wantsTags,
    ]
  );

  /** Queries */
  const [getAdditionalVideoByCandidateId] =
    useLazyGetAdditionalVideosByCandidateIdQuery();
  const [getCandidateEducation] = useLazyGetCandidateEducationQuery();
  const [getCandidatesJobInterest] = useLazyGetCandidateJobInterestQuery();
  const [getCandidateLanguages] = useLazyGetCandidateLanguagesQuery();
  const [getCandidateSkills] = useLazyGetCandidateSkillsQuery();
  const [getCV] = useLazyGetCvQuery();
  const [getCandidateWorkEHistory] = useLazyGetWorkHistoryByCandidateIdQuery();
  const [getAddtionalDocuments] =
    useLazyGetAdditionalDocumentsByCandidateIdQuery();
  const [getAdditionalTags] = useLazyGetAdditionalTagsByCandidateIdQuery();
  const [getEnglishAssessment] = useLazyGetEnglishAssessmentQuery();
  const [getCandidateInterviewByCPS] = useLazyGetCandidateInterviewByCPSQuery();
  const [getCandidateSmartInterviewsVideos] =
    useLazyGetCandidateSmartInterviewsVideosQuery();
  const [getCandidateReferrals] = useLazyGetCandidateReferralsQuery();

  const candidates = useMemo(
    () => sortedUniqueCandidates(position),
    [position]
  );

  /**
   * Fetched fields by candidate id. The ref is the source of truth for the
   * workers; the state mirrors it so the page re-renders as candidates land.
   */
  const hydratedRef = useRef<Record<number, HydratedCandidate>>({});
  const [hydrated, setHydrated] = useState(hydratedRef.current);
  const inFlightRef = useRef(new Set<number>());
  const workersRef = useRef(0);
  /** Bumped by refetch so results of an earlier load are dropped */
  const generationRef = useRef(0);
  /** After a refetch the network is the source, not the RTK cache */
  const forceRefetchRef = useRef(false);
  const mountedRef = useRef(true);

  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const flagsRef = useRef(resolvedFlags);
  flagsRef.current = resolvedFlags;
  const activeStatusRef = useRef(activeStatus);
  activeStatusRef.current = activeStatus;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Fetches the given fields (all of them when null) for one candidate */
  const fetchFields = useCallback(
    async (
      item: CandidatePositionStatusItem,
      flagFields: string[] | null,
      preferCacheValue: boolean
    ) => {
      const candidateId = item.Candidate.Id;
      const wants = (field: string) =>
        flagFields === null
          ? FLAG_FIELDS.some(
              ([flag, flagField]) =>
                flagField === field && flagsRef.current[flag]
            )
          : flagFields.includes(field);
      const tempData: any = {};

      await Promise.all([
        flagFields === null
          ? Promise.all([
              getAdditionalVideoByCandidateId(
                {
                  candidateId,
                  lang: userLanguage,
                },
                preferCacheValue
              ).then((response) => {
                const tempVideoData =
                  response?.data?.map((singleVideo) => {
                    const tempSingleVideo: any = { ...singleVideo };
                    delete tempSingleVideo.CreateTime;
                    delete tempSingleVideo.IsVisible;
                    return tempSingleVideo;
                  }) || [];
                tempData.AdditionalVideos = tempVideoData;
              }),
              getCandidatesJobInterest(
                {
                  candidateId,
                  lang: userLanguage,
                },
                preferCacheValue
              ).then((response: any) => {
                tempData.CandidateJobInterest = response.data
                  ? {
                      Id: response?.data?.Id,
                      SalaryLowerBound: response.data?.Salary[0],
                      SalaryUpperBound: response.data?.Salary[1],
                    }
                  : null;
              }),
              getCandidateSkills(
                {
                  candidateId,
                  lang: userLanguage,
                },
                preferCacheValue
              ).then((response) => {
                tempData.CandidateSkills = [
                  ...(response?.data ? response.data : []),
                ].map((singleSkill) => {
                  return {
                    Id: singleSkill?.Id,
                    Skill: singleSkill?.Skill,
                    IsVerified: !!singleSkill?.IsVerified.val,
                  };
                });
              }),
              getCV({ candidateId }, preferCacheValue).then((response) => {
                tempData.Curriculum = {
                  Url: response?.data?.Url,
                  Id: response?.data?.Id,
                };
              }),
              getCandidateWorkEHistory(
                {
                  candidateId,
                  lang: userLanguage,
                },
                preferCacheValue
              ).then((response) => {
                tempData.CandidateWorkExperience = (
                  response?.data ? [...response.data] : []
                ).map((singleWorkHistory) => {
                  return {
                    Admission: singleWorkHistory?.Admission?.val
                      ? new Date(
                          singleWorkHistory?.Admission?.val
                        ).toISOString()
                      : null,
                    Company: singleWorkHistory?.Company?.val || null,
                    Egress: singleWorkHistory?.Egress?.val
                      ? new Date(singleWorkHistory?.Egress?.val).toISOString()
                      : null,
                    Id: singleWorkHistory?.Id,
                    JobTitle: singleWorkHistory?.JobTitle.val || null,
                  };
                });
                tempData.YearsOfExperience = computeYearsOfExperience(
                  tempData.CandidateWorkExperience
                );
              }),
              getAddtionalDocuments(
                {
                  candidateId,
                  lang: userLanguage,
                },
                preferCacheValue
              ).then((response) => {
                tempData.Files = (response?.data ? response.data : []).map(
                  (singleFile) => {
                    const tempSingleFile: any = { ...singleFile };
                    delete tempSingleFile.Key;
                    delete tempSingleFile.IsCandidatesVisible;
                    // IsEmployersVisible is kept: the showcase hides the file
                    // from employers when it is false
                    return tempSingleFile;
                  }
                );
              }),
              getCandidateSmartInterviewsVideos(
                {
                  candidateId,
                  status: SmartInterviewVideoStatus.Completed,
                },
                preferCacheValue
              ).then((response) => {
                tempData.SmartInterviewsVideos = (response?.data || []).map(
                  (singleSmartInterviewVideo) => {
                    const tempSingleSmartInterviewVideo: any = {
                      ...singleSmartInterviewVideo,
                    };
                    delete tempSingleSmartInterviewVideo.CreateTime;
                    delete tempSingleSmartInterviewVideo.IsVisible;
                    return tempSingleSmartInterviewVideo;
                  }
                );
              }),
              getCandidateReferrals(
                {
                  candidateId,
                  status: ReferralStatus.COMPLETED,
                },
                preferCacheValue
              ).then((response) => {
                tempData.CandidateReferrals = response?.data || [];
              }),
            ])
          : Promise.resolve(),
        wants('CandidateEducation')
          ? getCandidateEducation(
              {
                candidateId,
                lang: userLanguage,
              },
              preferCacheValue
            ).then((response) => {
              tempData.CandidateEducation = mapEducation(response?.data);
            })
          : Promise.resolve(),
        wants('CandidateLanguages')
          ? getCandidateLanguages(
              {
                candidateId,
                lang: userLanguage,
              },
              preferCacheValue
            ).then((response) => {
              tempData.CandidateLanguages = mapLanguages(response?.data);
            })
          : Promise.resolve(),
        wants('AdditionalTags')
          ? getAdditionalTags(
              {
                candidateId,
              },
              preferCacheValue
            ).then((response) => {
              tempData.AdditionalTags = mapTags(response?.data);
            })
          : Promise.resolve(),
        wants('Videos')
          ? getEnglishAssessment({ candidateId }, preferCacheValue).then(
              (resp) => {
                tempData.Videos = mapEnglishVideos(resp?.data);
              }
            )
          : Promise.resolve(),
        wants('CandidateInterviewStatus')
          ? getCandidateInterviewByCPS(
              {
                candidateId,
                candidatePositionStatus: item.Id,
              },
              preferCacheValue
            ).then((resp) => {
              tempData.CandidateInterviewStatus = resp?.data;
            })
          : Promise.resolve(),
      ]);

      return tempData;
    },
    [
      getAdditionalTags,
      getAdditionalVideoByCandidateId,
      getAddtionalDocuments,
      getCV,
      getCandidateEducation,
      getCandidateInterviewByCPS,
      getCandidateLanguages,
      getCandidateReferrals,
      getCandidateSkills,
      getCandidateSmartInterviewsVideos,
      getCandidateWorkEHistory,
      getCandidatesJobInterest,
      getEnglishAssessment,
    ]
  );

  /** The next candidate to fetch: the active tab first, then display order */
  const pickNext = useCallback(() => {
    const status = activeStatusRef.current;
    const waiting = candidatesRef.current.filter(
      (item) =>
        !inFlightRef.current.has(item.Candidate.Id) &&
        needsFetch(hydratedRef.current[item.Candidate.Id], flagsRef.current)
    );
    if (!filtersByStatus(status)) return waiting[0];
    return (
      waiting.find((item) => hasStatus(item, status as string)) ?? waiting[0]
    );
  }, []);

  /** Keeps fetching candidates, one at a time, until none is left */
  const runWorker = useCallback(async () => {
    workersRef.current += 1;
    try {
      let item = pickNext();
      while (item && mountedRef.current) {
        const candidateId = item.Candidate.Id;
        const generation = generationRef.current;
        const inFlight = inFlightRef.current;
        inFlight.add(candidateId);
        const previous = hydratedRef.current[candidateId];
        let next: HydratedCandidate;
        try {
          const fields = await fetchFields(
            item,
            missingFlagFields(previous, flagsRef.current),
            !forceRefetchRef.current
          );
          next = { fields: { ...previous?.fields, ...fields } };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Could not load candidate ${candidateId}`, error);
          next = { fields: { ...previous?.fields }, failed: true };
        }
        inFlight.delete(candidateId);
        if (generation === generationRef.current && mountedRef.current) {
          hydratedRef.current = { ...hydratedRef.current, [candidateId]: next };
          setHydrated(hydratedRef.current);
        }
        item = pickNext();
      }
    } finally {
      workersRef.current -= 1;
    }
  }, [fetchFields, pickNext]);

  const startWorkers = useCallback(() => {
    const idle = HYDRATION_CONCURRENCY - workersRef.current;
    for (let i = 0; i < idle; i += 1) runWorker();
  }, [runWorker]);

  // New candidates, or a filter that needs a field not fetched yet
  useEffect(() => {
    startWorkers();
  }, [candidates, resolvedFlags, startWorkers]);

  const refetch = useCallback(() => {
    generationRef.current += 1;
    forceRefetchRef.current = true;
    inFlightRef.current = new Set();
    hydratedRef.current = {};
    setHydrated(hydratedRef.current);
    startWorkers();
  }, [startWorkers]);

  /** Loaded candidates in display order, with the status the position has now */
  const data = useMemo(() => {
    if (!position?.CandidatesPositionStatus) return undefined;
    return candidates
      .filter((item) => {
        const entry = hydrated[item.Candidate.Id];
        return entry && !entry.failed;
      })
      .map((item) => ({
        ...positionCandidate(item),
        ...hydrated[item.Candidate.Id].fields,
      }));
  }, [candidates, hydrated, position]);

  const pending = useMemo(
    () =>
      candidates.filter((item) =>
        needsFetch(hydrated[item.Candidate.Id], resolvedFlags)
      ),
    [candidates, hydrated, resolvedFlags]
  );
  const isLoading = pending.length > 0;
  const isLoadingActiveStatus = filtersByStatus(activeStatus)
    ? pending.some((item) => hasStatus(item, activeStatus as string))
    : isLoading;

  return {
    data,
    isLoading,
    /** Whether candidates of the active tab are still loading */
    isLoadingActiveStatus,
    refetch,
  };
};

export default GetCandidateDataByMultipleFetch;
